import { ApiClient } from '@/config/axios.config';
import type {
  FixedDaysPool,
  FixedDaysSummary,
  FixedDaysPoolParticipantSummary,
  FixedDaysHistory,
  FixedDaysFile,
} from '@/types/fixedDays.types';

export class FixedDaysService {
  private static readonly BASE_PATH = '/fixeddays';

  /**
   * Get all available Fixed Days pools
   */
  static async getPools(): Promise<FixedDaysPool[]> {
    try {
      return await ApiClient.get<FixedDaysPool[]>(`${this.BASE_PATH}/pool`);
    } catch (error) {
      console.error('Failed to fetch Fixed Days pools:', error);
      throw error;
    }
  }

  /**
   * Get history/period entries for a specific pool
   * @param poolId - The pool identifier
   */
  static async getHistory(poolId: number): Promise<FixedDaysHistory[]> {
    try {
      return await ApiClient.get<FixedDaysHistory[]>(`${this.BASE_PATH}/history/${poolId}`);
    } catch (error) {
      console.error(`Failed to fetch Fixed Days history for poolId ${poolId}:`, error);
      throw error;
    }
  }

  /**
   * Get Fixed Days summary data for a specific file ID
   * @param fileId - The unique file identifier
   */
  static async getSummary(fileId: number): Promise<FixedDaysSummary[]> {
    try {
      const summaries = await ApiClient.get<FixedDaysSummary[]>(
        `${this.BASE_PATH}/summary/${fileId}`
      );

      // Transform pwFixedPercent to percentage (multiply by 100)
      return summaries.map((summary) => ({
        ...summary,
        pwFixedPercent: summary.pwFixedPercent * 100,
        spotRateInclRelet: Number(summary.spotRateInclRelet) || 0,
      }));
    } catch (error) {
      console.error(`Failed to fetch Fixed Days summary for fileId ${fileId}:`, error);
      throw error;
    }
  }

  /**
   * Get the file metadata (including explanation text) for a specific file ID
   * @param fileId - The unique file identifier
   */
  static async getFile(fileId: number): Promise<FixedDaysFile> {
    try {
      return await ApiClient.get<FixedDaysFile>(`${this.BASE_PATH}/file/${fileId}`);
    } catch (error) {
      console.error(`Failed to fetch Fixed Days file for fileId ${fileId}:`, error);
      throw error;
    }
  }

  /**
   * Get pool participants for a specific file ID
   * @param fileId - The unique file identifier
   */
  static async getParticipants(fileId: number): Promise<string[]> {
    try {
      return await ApiClient.get<string[]>(`${this.BASE_PATH}/poolparticipants/${fileId}`);
    } catch (error) {
      console.error(`Failed to fetch pool participants for fileId ${fileId}:`, error);
      throw error;
    }
  }

  /**
   * Get pool participants summary for a specific file ID and participant name
   * @param fileId - The unique file identifier
   * @param participantName - Optional participant name filter
   */
  static async getParticipantsSummary(
    fileId: number,
    participantName?: string
  ): Promise<Record<number, FixedDaysPoolParticipantSummary>> {
    if (!participantName) {
      return {};
    }

    try {
      const summaries = await ApiClient.get<FixedDaysPoolParticipantSummary[]>(
        `${this.BASE_PATH}/poolparticipants/summary/${fileId}/${participantName}`
      );

      // Transform array to keyed object by sectionID
      return summaries.reduce(
        (acc, summary) => {
          acc[summary.sectionID] = summary;
          return acc;
        },
        {} as Record<number, FixedDaysPoolParticipantSummary>
      );
    } catch (error) {
      console.error(
        `Failed to fetch participants summary for fileId ${fileId}, participant ${participantName}:`,
        error
      );
      throw error;
    }
  }
}
